import { Button } from '@mui/material'
import { forwardRef, useEffect } from 'react'
import { Divider, TextInputField, TextInputFieldProps } from 'utoniq-ui'

import { Maybe, UserDeliveryInfoFragment } from '@/clients/utoniq-core/schema'
import { handleError, tLabel, useMessage, useTranslation } from '@/core'
import { Cell, CellFrame, Organism } from '@/core/architecture'
import { PostalCode } from '@/core/entities/values/PostalCode'
import { Form, useForm, UseFormProps } from '@/core/form'
import {
  validateCity,
  validateFullName,
  validateLine1,
  validateLine2,
  validatePhoneNumber,
  validatePrefecture,
} from '@/core/validators'

export type Props = {
  deliveryInfo: Omit<UserDeliveryInfoFragment, 'isPhoneNumberVerified' | 'id'>
  onSubmitSetting: (inputs: SettingValues) => Promise<void>
}

export type SettingValues = {
  phoneNumber: string
  fullName: string
  postalCode: string
  prefecture: string
  city: string
  line1: string
  line2?: Maybe<string>
}

const schema: UseFormProps['schema'] = o => ({
  phoneNumber: validatePhoneNumber(o, 'deliveryInfoSetting.phoneNumber'),
  fullName: validateFullName(o, 'deliveryInfoSetting.fullName'),
  postalCode: o
    .string()
    .custom(PostalCode.createValidator())
    .label(tLabel('deliveryInfoSetting.postalCode')),
  prefecture: validatePrefecture(o, 'deliveryInfoSetting.prefecture'),
  city: validateCity(o, 'deliveryInfoSetting.city'),
  line1: validateLine1(o, 'deliveryInfoSetting.line1'),
  line2: validateLine2(o, 'deliveryInfoSetting.line2'),
})

export const DeliveryInfoSettingForm: Organism<Props> = ({
  deliveryInfo,
  onSubmitSetting,
}) => {
  const { t } = useTranslation('deliveryInfoSetting')
  const { showMessage, showError } = useMessage()
  const {
    handleSubmit,
    formState: { errors, isSubmitting },
    register,
    reset,
    setError,
  } = useForm<SettingValues>({
    schema,
    defaultValues,
  })

  useEffect(() => {
    if (!deliveryInfo) return

    const { phoneNumber, fullName, address } = deliveryInfo

    /**
     * @description
     * データをフォームに合わせるため、__typenameとfullAddressをaddressから除外する
     */
    const { fullAddress, __typename, ...addressInput } = (address ||
      {}) as UserDeliveryInfoFragment['address'] & { __typename: string }

    reset({
      phoneNumber: phoneNumber ?? '',
      fullName: fullName ?? '',
      ...addressInput,
    })
  }, [deliveryInfo, reset])

  const onSubmit = handleSubmit(values => {
    const normalizedValues = {
      ...values,
      postalCode: PostalCode.normalize(values.postalCode),
    }

    return onSubmitSetting(normalizedValues)
      .then(() => showMessage(t('submitSuccess'), 'success'))
      .catch(e => handleError<SettingValues>(e, setError, showError))
  })

  return (
    <Form onSubmit={onSubmit} sx={{ py: 3 }}>
      <TextInputCell
        type="tel"
        error={errors?.phoneNumber}
        {...register('phoneNumber')}
      />
      <DividerCell />
      <TextInputCell
        type="text"
        error={errors?.fullName}
        {...register('fullName')}
      />
      <TextInputCell
        type="tel"
        textFieldProps={{
          InputProps: {
            inputProps: {
              pattern: '\\d{3}-?\\d{4}|\\d{7}',
              inputMode: 'tel',
              sx: { px: 4, py: 3 },
            },
          },
        }}
        error={errors?.postalCode}
        {...register('postalCode')}
      />
      <TextInputCell
        type="text"
        error={errors?.prefecture}
        {...register('prefecture')}
      />
      <TextInputCell type="text" error={errors?.city} {...register('city')} />
      <TextInputCell type="text" error={errors?.line1} {...register('line1')} />
      <TextInputCell type="text" error={errors?.line2} {...register('line2')} />
      <ButtonCell loading={isSubmitting} />
    </Form>
  )
}

const TextInputCell = forwardRef(
  (props: Omit<TextInputFieldProps, 't'>, ref) => {
    const { t } = useTranslation('deliveryInfoSetting')
    return (
      <CellFrame>
        <TextInputField {...props} ref={ref} t={t} />
      </CellFrame>
    )
  }
)

type ButtonCellProps = {
  loading: boolean
}
const ButtonCell: Cell<ButtonCellProps> = ({ loading }) => {
  const { t } = useTranslation('deliveryInfoSetting')
  return (
    <CellFrame sx={{ py: 5, textAlign: 'center' }}>
      <Button type="submit" size="large" sx={{ width: 244 }} loading={loading}>
        {t('submit')}
      </Button>
    </CellFrame>
  )
}

const DividerCell: Cell = () => {
  return (
    <CellFrame sx={{ pb: 5 }}>
      <Divider />
    </CellFrame>
  )
}

const defaultValues: SettingValues = {
  phoneNumber: '',
  fullName: '',
  postalCode: '',
  prefecture: '',
  city: '',
  line1: '',
  line2: '',
}
